import React, { useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import Button from 'react-bootstrap/Button';
import { DateRangePicker } from 'react-date-range';
import { useTranslate } from '@tolgee/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays } from '@fortawesome/pro-solid-svg-icons';

/**
 * Date range constants
 * These constants define all possible date range options available in the component
 */
export const DATE_RANGES = {
  LAST_SEVEN_DAYS: 'last_seven_days',
  THIS_MONTH: 'this_month',
  LAST_30_DAYS: 'last_30_days',
  LAST_MONTH: 'last_month',
  LAST_3_MONTHS: 'last_3_months',
  LAST_6_MONTHS: 'last_6_months',
  LAST_12_MONTHS: 'last_12_months',
  CUSTOM_RANGE: 'custom_range'
};

interface LomavisDateRangeInput1Props {
  currentLang: {
    label: string;
    value: string;
    systemValue: string;
    icon: string;
  };
  setDates: (value: DateRange) => void;
  demo: boolean;
  defaultIndex?: number;
  /**
   * Optional array of date range labels to display.
   * If not provided, all ranges will be shown.
   * Example: [DATE_RANGES.LAST_SEVEN_DAYS, DATE_RANGES.LAST_MONTH, DATE_RANGES.CUSTOM_RANGE]
   */
  availableRanges?: string[];
  /**
   * Allow selecting the current day in the date picker.
   * By default, the date picker only allows selecting up to yesterday.
   */
  allowCurrentDay?: boolean;
}
interface DateRange {
  startDate: Date;
  endDate: Date;
}
interface SelectionRange {
  startDate: Date;
  endDate: Date;
  key: string;
  active?: string;
}

export const getDefaultRanges = (allowCurrentDay: boolean = false): any => {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const lastSevenDays = new Date();
  lastSevenDays.setDate(today.getDate() - 7);
  const thisWeekStart = new Date(today);
  thisWeekStart.setDate(today.getDate() - today.getDay());
  const thisWeekEnd = new Date(thisWeekStart);
  thisWeekEnd.setDate(thisWeekStart.getDate() + 6);

  const lastWeekStart = new Date(thisWeekStart);
  lastWeekStart.setDate(thisWeekStart.getDate() - 7);
  const lastWeekEnd = new Date(thisWeekEnd);
  lastWeekEnd.setDate(thisWeekEnd.getDate() - 7);

  const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);

  const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

  const last30DaysStart = new Date(today);
  last30DaysStart.setDate(today.getDate() - (allowCurrentDay ? 29 : 30));

  const last60DaysStart = new Date(today);
  last60DaysStart.setDate(today.getDate() - (allowCurrentDay ? 59 : 60));
  
  // Use today or yesterday as the end date based on allowCurrentDay
  const endDate = allowCurrentDay ? today : yesterday;

  return [
    {
      label: DATE_RANGES.LAST_SEVEN_DAYS,
      startDate: lastSevenDays,
      endDate: endDate
    },
    {
      label: DATE_RANGES.THIS_MONTH,
      startDate: thisMonthStart,
      endDate: endDate
    },
    {
      label: DATE_RANGES.LAST_30_DAYS,
      startDate: last30DaysStart,
      endDate: endDate
    },
    {
      label: DATE_RANGES.LAST_MONTH,
      startDate: lastMonthStart,
      endDate: lastMonthEnd
    },
    {
      label: DATE_RANGES.LAST_3_MONTHS,
      startDate: new Date(today.getFullYear(), today.getMonth() - 3, 1),
      endDate: new Date(today.getFullYear(), today.getMonth(), 0)
    },
    {
      label: DATE_RANGES.LAST_6_MONTHS,
      startDate: new Date(today.getFullYear(), today.getMonth() - 6, 1),
      endDate: new Date(today.getFullYear(), today.getMonth(), 0)
    },
    {
      label: DATE_RANGES.LAST_12_MONTHS,
      startDate: new Date(today.getFullYear(), today.getMonth() - 12, 1),
      endDate: new Date(today.getFullYear(), today.getMonth(), 0)
    },
    {
      label: DATE_RANGES.CUSTOM_RANGE,
      startDate: endDate,
      endDate: endDate
    }
  ];
};
const yesterday = new Date();
yesterday.setDate(yesterday.getDate() - 1);

/**
 * Atoms: LomavisDateRangeInput1
 *
 * A date range picker component that allows users to select predefined date ranges or custom date ranges.
 * 
 * Available date ranges:
 * - DATE_RANGES.LAST_SEVEN_DAYS: Last 7 days from yesterday
 * - DATE_RANGES.THIS_MONTH: From the beginning of the current month to yesterday
 * - DATE_RANGES.LAST_30_DAYS: Last 30 days from yesterday
 * - DATE_RANGES.LAST_MONTH: The entire previous month
 * - DATE_RANGES.LAST_3_MONTHS: From 3 months ago to the end of last month
 * - DATE_RANGES.LAST_6_MONTHS: From 6 months ago to the end of last month
 * - DATE_RANGES.LAST_12_MONTHS: From 12 months ago to the end of last month
 * - DATE_RANGES.CUSTOM_RANGE: User-defined custom date range
 * 
 * You can filter available ranges using the availableRanges prop.
 */
export const LomavisDateRangeInput1: React.FC<LomavisDateRangeInput1Props> = ({
  currentLang,
  setDates,
  demo,
  defaultIndex = 0,
  availableRanges,
  allowCurrentDay = false
}) => {
  const { t } = useTranslate();
  const myRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const allDefaultRanges = getDefaultRanges(allowCurrentDay);
  
  // Filter ranges if availableRanges is provided
  const defaultRanges = availableRanges 
    ? allDefaultRanges.filter(range => availableRanges.includes(range.label))
    : allDefaultRanges;
  
  // Ensure defaultIndex is valid for the filtered ranges
  const validDefaultIndex = defaultRanges.length > defaultIndex ? defaultIndex : 0;
  const DEFAULT_INDEX = validDefaultIndex;
  
  const [show, setShow] = useState(false);
  const [ranges, setRanges] = useState<any[]>([]);
  const [shortRange, setShortRange] = useState(false);
  const [buttonMessage, setButtonMessage] = useState(
    defaultRanges.length > 0 ? t(defaultRanges[DEFAULT_INDEX].label) : ''
  );

  // Default to yesterday if no ranges are available
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  
  const [selectionRange, setSelectionRange] = useState<SelectionRange>({
    startDate: defaultRanges.length > 0 ? defaultRanges[DEFAULT_INDEX].startDate : yesterday,
    endDate: defaultRanges.length > 0 ? defaultRanges[DEFAULT_INDEX].endDate : yesterday,
    key: 'selection',
    active: defaultRanges.length > 0 ? defaultRanges[DEFAULT_INDEX].label : DATE_RANGES.CUSTOM_RANGE
  });

  const handleSelect = (ranges: any): void => {
    setSelectionRange({
      ...selectionRange,
      startDate: ranges.selection.startDate,
      endDate: ranges.selection.endDate,
      active: ranges.selection.key
    });
  };

  const getLabel = (start: Date, end: Date): string => {
    const formatedStart = start.toLocaleDateString(currentLang.value);
    const formatedEnd = end.toLocaleDateString(currentLang.value);
    
    // Special handling for predefined month ranges
    const today = new Date();
    
    // Last month range
    const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    
    // Last 3 months range
    const last3MonthsStart = new Date(today.getFullYear(), today.getMonth() - 3, 1);
    const last3MonthsEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    
    // Last 6 months range
    const last6MonthsStart = new Date(today.getFullYear(), today.getMonth() - 6, 1);
    const last6MonthsEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    
    // Last 12 months range
    const last12MonthsStart = new Date(today.getFullYear(), today.getMonth() - 12, 1);
    const last12MonthsEnd = new Date(today.getFullYear(), today.getMonth(), 0);
    
    // Check if the selected range matches any of the predefined ranges
    // by comparing year, month, and day components separately
    const isLastMonth = 
      start.getFullYear() === lastMonthStart.getFullYear() &&
      start.getMonth() === lastMonthStart.getMonth() &&
      start.getDate() === lastMonthStart.getDate() &&
      end.getFullYear() === lastMonthEnd.getFullYear() &&
      end.getMonth() === lastMonthEnd.getMonth() &&
      end.getDate() === lastMonthEnd.getDate();
    
    const isLast3Months = 
      start.getFullYear() === last3MonthsStart.getFullYear() &&
      start.getMonth() === last3MonthsStart.getMonth() &&
      start.getDate() === last3MonthsStart.getDate() &&
      end.getFullYear() === last3MonthsEnd.getFullYear() &&
      end.getMonth() === last3MonthsEnd.getMonth() &&
      end.getDate() === last3MonthsEnd.getDate();
    
    const isLast6Months = 
      start.getFullYear() === last6MonthsStart.getFullYear() &&
      start.getMonth() === last6MonthsStart.getMonth() &&
      start.getDate() === last6MonthsStart.getDate() &&
      end.getFullYear() === last6MonthsEnd.getFullYear() &&
      end.getMonth() === last6MonthsEnd.getMonth() &&
      end.getDate() === last6MonthsEnd.getDate();
    
    const isLast12Months = 
      start.getFullYear() === last12MonthsStart.getFullYear() &&
      start.getMonth() === last12MonthsStart.getMonth() &&
      start.getDate() === last12MonthsStart.getDate() &&
      end.getFullYear() === last12MonthsEnd.getFullYear() &&
      end.getMonth() === last12MonthsEnd.getMonth() &&
      end.getDate() === last12MonthsEnd.getDate();
    
    if (isLastMonth) {
      return t(DATE_RANGES.LAST_MONTH);
    } else if (isLast3Months) {
      return t(DATE_RANGES.LAST_3_MONTHS);
    } else if (isLast6Months) {
      return t(DATE_RANGES.LAST_6_MONTHS);
    } else if (isLast12Months) {
      return t(DATE_RANGES.LAST_12_MONTHS);
    }
    
    // For other ranges, use the original matching logic
    const match = defaultRanges.find(
      (item: SelectionRange) =>
        item.startDate.toLocaleDateString(currentLang.value) ===
          formatedStart &&
        item.endDate.toLocaleDateString(currentLang.value) === formatedEnd
    );
    return match ? t(match.label) : `${formatedStart} - ${formatedEnd}`;
  };

  const handleApply = () => {
    const minRangeLength = 5;
    const diffInDays = Math.ceil(
      ((selectionRange.endDate as any) - (selectionRange.startDate as any)) /
        (1000 * 60 * 60 * 24)
    );

    // Check if the selected range meets the minimum length requirement
    if (diffInDays >= minRangeLength) {
      setShortRange(false);
      setDates({
        startDate: selectionRange.startDate,
        endDate: selectionRange.endDate
      });
      setButtonMessage(
        getLabel(selectionRange.startDate, selectionRange.endDate)
      );
      setShow(false);
    } else setShortRange(true);
  };

  useEffect(() => {
    const ranges: any[] = defaultRanges.map((item: any) => {
      return {
        label: t(item.label),
        range: () => ({
          startDate: item.startDate,
          endDate: item.endDate,
          key: item.label
        }),
        isSelected: (dateRange: any) => {
          return dateRange.active === item.label;
        }
      };
    });
    setRanges(ranges);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (
        !buttonRef.current?.contains(event.target as Node) &&
        myRef.current &&
        !myRef.current.contains(event.target as Node)
      ) {
        setShow(false);
      }
    }
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [myRef]);

  return (
    <div className=" ">
      <OverlayTrigger
        show={show}
        placement="bottom"
        trigger="click"
        overlay={
          <Popover className="datePickerpopover ">
            <div className="bg-white d-inline-block" ref={myRef}>
              <DateRangePicker
                maxDate={allowCurrentDay ? new Date() : new Date(new Date().setDate(new Date().getDate() - 1))}
                ranges={[selectionRange]}
                staticRanges={ranges}
                onChange={handleSelect}
                months={1}
                inputRanges={[]}
                weekStartsOn={currentLang.systemValue === 'en' ? 0 : 1}
              />
              <div className="d-flex justify-content-end w-100">
                <Button
                  variant="secondary"
                  size="sm"
                  className="m-2 "
                  onClick={() => {
                    setShow(false);
                  }}
                >
                  {t('cancel')}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  className="m-2"
                  onClick={handleApply}
                >
                  {t('apply')}
                </Button>
              </div>{' '}
              {demo && (
                <div className="d-flex justify-content-center alert-warning align-items-center py-2 text-warning fw-bolder">
                  {t('its_not_possible_to_change_date_range_on_demo_location')}
                </div>
              )}
              {shortRange && (
                <div className="d-flex justify-content-center alert-warning align-items-center py-2 text-warning fw-bolder">
                  {t('range_should_at_least_be_5_days_long')}
                </div>
              )}
            </div>
          </Popover>
        }
      >
        <div ref={buttonRef}>
          <Button
            variant="light"
            size="sm"
            onClick={() => {
              setShow(!show);
            }}
            className="fs-7 d-flex align-items-center bg-white h-100 shadow w-100"
            role="button"
          >
            <FontAwesomeIcon
              icon={faCalendarDays}
              size="1x"
              className=" text-dark"
            />
            <span className="mx-1 text-dark"> {buttonMessage}</span>&nbsp;
          </Button>
        </div>
      </OverlayTrigger>
    </div>
  );
};
